'use client';

import type { ReactNode } from 'react';
import React, { useContext, useRef } from 'react';
import type { HTMLMotionProps, Variant } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { mergeProps } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import { CollapseContext } from './CollapseContext';

const transition = {
  ease: theme.transitions.curves.primary,
  duration: 0.3,
};

const animationVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition,
  },
  open: {
    height: 'auto',
    opacity: 1,
    transition,
  },
};

const childVariants = {
  collapsed: { y: -8, transition },
  open: { y: 0, transition },
};

type OverflowOptions = 'hidden' | 'visible';

export type CollapseContentProps = {
  children: ReactNode;
  animateOnMount?: boolean;
  variants?: {
    collapsed: Variant;
    open: Variant;
  };
  overflow?: OverflowOptions;
} & HTMLMotionProps<'div'>;

export function CollapseContent({
  children,
  variants = animationVariants,
  animateOnMount = false,
  overflow = 'hidden',
  style,
  ...other
}: CollapseContentProps) {
  const context = useContext(CollapseContext);
  const ref = useRef(null);

  if (process.env.NODE_ENV === 'development') {
    if (!context) {
      throw new Error(
        'CollapseContent must be used within a Collapse element.'
      );
    }
  }

  const { isShowing, bindCollapse, intentToAnimate } = context;

  return (
    <AnimatePresence initial={animateOnMount}>
      {isShowing && (
        <motion.div
          ref={ref}
          animate="open"
          data-testid="collapse-content"
          exit="collapsed"
          initial="collapsed"
          variants={variants}
          {...(mergeProps(bindCollapse, other))}
          style={{
            ...style,
            willChange: intentToAnimate ? 'height, opacity' : 'none',
            overflow,
          }}
        >
          <motion.div variants={childVariants}>{children}</motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
